import classNames from 'classnames';
import * as React from 'react';

import './internal-video.style.scss';

import type { InternalVideoProps } from './internal-video.props';

const InternalVideo: React.FC<InternalVideoProps> = function(props) {
  const {
          controls,
          src,
          thumbnail,
        } = props;

  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  const [backdrop, setBackdrop]   = React.useState<string>();
  const [isPlaying, setIsPlaying] = React.useState(false);
  // crossOrigin is only needed to grab a frame for the backdrop (canvas). When
  // the file host doesn't answer with CORS headers for this origin (e.g. a test
  // domain against the prod file host), a CORS-mode request fails outright and
  // the video never plays — so on error fall back to a plain request.
  const [useCors, setUseCors]     = React.useState(true);

  // The server-rendered <video> starts loading before hydration, so a CORS
  // failure can happen before onError below is even attached — check once
  // the component is live.
  React.useEffect(() => {
    if (useCors && videoRef.current?.error) {
      setUseCors(false);
    }
    // Changing the crossorigin attribute alone doesn't restart loading.
    if (!useCors) {
      videoRef.current?.load();
    }
  }, [useCors]);

  const pause  = React.useCallback(() => {
    videoRef.current?.pause();
    setIsPlaying(false);
  }, []);
  const play   = React.useCallback(() => {
    videoRef.current?.play()
      .then(() => {
        setIsPlaying(true);
      });
  }, []);
  const toggle = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if (!thumbnail) {
      if (isPlaying) {
        pause();
      } else {
        play();
      }
    }
  }, [isPlaying, pause, play, thumbnail]);

  return (
    <div className={classNames('internal-video', { thumbnail })}>
      {backdrop && (
        <div className={'backdrop'}>
          <img
            alt={''}
            src={backdrop}
          />
        </div>
      )}
      <video
        controls={controls}
        crossOrigin={useCors ? 'anonymous' : undefined}
        muted={!backdrop}
        playsInline
        preload={!backdrop ? 'auto' : 'metadata'}
        ref={videoRef}
        src={src}
        onError={() => {
          if (useCors) {
            setUseCors(false);
          }
        }}
        onEnded={() => {
          if (videoRef.current) {
            videoRef.current.currentTime = 0;
          }

          setIsPlaying(false);
        }}
        onLoadedMetadata={() => {
          if (videoRef.current) {
            videoRef.current.currentTime = 0.1;
          }
        }}
        onPause={() => {
          setIsPlaying(false);
        }}
        onSeeked={() => {
          if (videoRef.current) {
            const canvas = document.createElement('canvas');

            canvas.height = videoRef.current.videoHeight;
            canvas.width  = videoRef.current.videoWidth;

            const ctx = canvas.getContext('2d');

            if (ctx) {
              ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

              try {
                setBackdrop(canvas.toDataURL('image/jpeg'));
              } catch (error) {
                // Loaded without CORS (see useCors): the canvas is tainted, no backdrop.
              }

              canvas.remove();
            }
          }
        }}
      />
      {!controls && (
        <div
          className={classNames('overlay', { 'playing': isPlaying })}
          onClick={toggle}
        >
          <div className={'play-pause'}>
            <svg
              height={40}
              width={40}
              version={'1.1'}
              xmlns={'http://www.w3.org/2000/svg'}
              viewBox={'0 0 512 512'}
            >
              <g>
                <path
                  d={'M256,0C114.625,0,0,114.625,0,256c0,141.374,114.625,256,256,256c141.374,0,256-114.626,256-256 C512,114.625,397.374,0,256,0z M351.062,258.898l-144,85.945c-1.031,0.626-2.344,0.657-3.406,0.031 c-1.031-0.594-1.687-1.702-1.687-2.937v-85.946v-85.946c0-1.218,0.656-2.343,1.687-2.938c1.062-0.609,2.375-0.578,3.406,0.031 l144,85.962c1.031,0.586,1.641,1.718,1.641,2.89C352.703,257.187,352.094,258.297,351.062,258.898z'}
                  fill={'#ffffff'}
                />
              </g>
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalVideo;